import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, X, Users, Phone, Building, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface FilterState {
  name_ar: string;
  name_en: string;
  mobile: string;
  company: string;
  status: string;
  generalSearch: string;
  created_at_from: string;
  created_at_to: string;
}

interface ClientAdvancedFilterProps {
  onFilterChange: (filters: FilterState) => void;
  statusOptions: Array<{ value: string; label: string; color: string }>;
  className?: string;
}

export function ClientAdvancedFilter({ onFilterChange, statusOptions, className }: ClientAdvancedFilterProps) {
  const [filters, setFilters] = useState<FilterState>({
    name_ar: '',
    name_en: '',
    mobile: '',
    company: '',
    status: '',
    generalSearch: '',
    created_at_from: '',
    created_at_to: ''
  });

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const emptyFilters = {
      name_ar: '',
      name_en: '',
      mobile: '',
      company: '',
      status: '',
      generalSearch: '',
      created_at_from: '',
      created_at_to: ''
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <div className={className}>
      {/* Header with Clear Button */}
      {hasActiveFilters && (
        <div className="flex justify-end mb-4">
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-2 text-destructive">
            <X className="h-4 w-4" />
            مسح الكل
          </Button>
        </div>
      )}

      {/* Filter Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* الاسم بالعربية */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            الاسم بالعربية
          </Label>
          <Input
            placeholder="البحث بالاسم العربي..."
            value={filters.name_ar}
            onChange={(e) => handleFilterChange('name_ar', e.target.value)}
          />
        </div>

        {/* الاسم بالإنجليزية */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            الاسم بالإنجليزية
          </Label>
          <Input
            placeholder="البحث بالاسم الإنجليزي..."
            value={filters.name_en}
            onChange={(e) => handleFilterChange('name_en', e.target.value)}
          />
        </div>

        {/* رقم الجوال */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            رقم الجوال
          </Label>
          <Input
            placeholder="البحث برقم الجوال..."
            value={filters.mobile}
            onChange={(e) => handleFilterChange('mobile', e.target.value)}
            dir="ltr"
          />
        </div>

        {/* الشركة */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            الشركة
          </Label>
          <Input
            placeholder="البحث بالشركة..."
            value={filters.company}
            onChange={(e) => handleFilterChange('company', e.target.value)}
          />
        </div>

        {/* الحالة */}
        <div className="space-y-2">
          <Label>حالة العميل</Label>
          <Select value={filters.status || undefined} onValueChange={(value) => handleFilterChange('status', value || '')}>
            <SelectTrigger>
              <SelectValue placeholder="جميع الحالات" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${option.color.replace('bg-', 'bg-').replace('text-', '').replace('border-', '')}`} />
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* تاريخ الإنشاء من */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            تاريخ الإنشاء من
          </Label>
          <Input
            type="date"
            value={filters.created_at_from}
            onChange={(e) => handleFilterChange('created_at_from', e.target.value)}
          />
        </div>

        {/* تاريخ الإنشاء إلى */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            تاريخ الإنشاء إلى
          </Label>
          <Input
            type="date"
            value={filters.created_at_to}
            onChange={(e) => handleFilterChange('created_at_to', e.target.value)}
          />
        </div>
      </div>

      {/* الفلاتر النشطة */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-4 mt-4 border-t">
          {filters.generalSearch && (
            <Badge variant="secondary" className="gap-1">
              بحث عام: {filters.generalSearch}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('generalSearch', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.name_ar && (
            <Badge variant="secondary" className="gap-1">
              اسم عربي: {filters.name_ar}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('name_ar', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.name_en && (
            <Badge variant="secondary" className="gap-1">
              اسم إنجليزي: {filters.name_en}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('name_en', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.mobile && (
            <Badge variant="secondary" className="gap-1">
              جوال: {filters.mobile}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('mobile', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.company && (
            <Badge variant="secondary" className="gap-1">
              شركة: {filters.company}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('company', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.status && (
            <Badge variant="secondary" className="gap-1">
              حالة: {statusOptions.find(s => s.value === filters.status)?.label || filters.status}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('status', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.created_at_from && (
            <Badge variant="secondary" className="gap-1">
              من تاريخ: {filters.created_at_from}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('created_at_from', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.created_at_to && (
            <Badge variant="secondary" className="gap-1">
              إلى تاريخ: {filters.created_at_to}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 w-4 h-4"
                onClick={() => handleFilterChange('created_at_to', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}